import React, { useCallback, useRef, useState } from 'react'

import { NextPage } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { cx } from '@linaria/core'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useFoodSWRInfinite } from '~/api-client/food_api'
import { Restaurant } from '~/api-definition/definition'
import { Button, ButtonGroup } from '~/components/action/Button'
import { Heading } from '~/components/data-display/Heading'
import { Checkbox } from '~/components/data-input/Checkbox'
import { SelectV2 } from '~/components/data-input/SelectV2'
import { TextField } from '~/components/data-input/TextField'
import { SearchIcon, TipsIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { restaurantAreaAndGenre } from '~/libs/restaurant'

const Page: NextPage = () => {
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const [searchRestaurantsInputValue, setSearchRestaurantsInputValue] =
    useState('')
  const [searchRestaurantsQuery, setSearchRestaurantsQuery] = useState('')

  const thumbnailImages = (restaurant: Restaurant) =>
    restaurant.thumbnailImages.length > 0
      ? [...Array(3)].map((_, i) => restaurant.thumbnailImages[i])
      : []

  const searchRestaurants = useFoodSWRInfinite(
    '/restaurant/search_restaurants',
    { key: searchRestaurantsQuery },
    (pageIndex, _) => {
      return {
        count: config.pageSize.restaurantList,
        offset: pageIndex * config.pageSize.restaurantList,
      }
    },
    { disabled: searchRestaurantsQuery === '' }
  )
  const flattenSearchRestaurants = searchRestaurants.data?.reduce(
    (acc, resp) => {
      return [...acc, ...resp.restaurants]
    },
    [] as Restaurant[]
  )

  const onClickSearchRestaurants = useCallback(() => {
    setSearchRestaurantsQuery(searchRestaurantsInputValue)
  }, [searchRestaurantsInputValue])

  const onChangeSearchRestaurantsInputValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchRestaurantsInputValue(e.target.value)
    },
    []
  )

  return (
    <>
      <Head>
        <title>探す</title>
      </Head>
      <Header>
        <div />
        探す
        <div />
      </Header>

      <Container>
        <ContainerInset>
          <div className="flex flex-col gap-5">
            <ButtonGroup className="w-full">
              <Link href="/search/foodies">
                <Button variant="functionWeak">フーディーを探す</Button>
              </Link>
              <Link href="/search/restaurants">
                <Button variant="functionWeak" data-current={true}>
                  店舗を探す
                </Button>
              </Link>
            </ButtonGroup>
            <div>
              <div className="mb-3">
                <TextField
                  leftIcon={<SearchIcon fill="#333" />}
                  inputProps={{
                    placeholder: '店舗名から検索',
                    ref: searchInputRef,
                    value: searchRestaurantsInputValue,
                    onBlur: onChangeSearchRestaurantsInputValue,
                    onChange: onChangeSearchRestaurantsInputValue,
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* TODO エリアとジャンルの仕様が決まってAPIが提供されたら実装する */}
                {false && (
                  <>
                    <div>
                      <SelectV2
                        name="search-area"
                        label="エリア"
                        fullWidth
                        options={[{ value: '1', label: 'ああ' }]}
                        defaultValue={'1'}
                      />
                    </div>
                    <div>
                      <SelectV2
                        name="search-genre"
                        label="ジャンル"
                        fullWidth
                        options={[{ value: '1', label: 'ああ' }]}
                        defaultValue={'1'}
                      />
                    </div>
                  </>
                )}
                {false && (
                  <>
                    <div>
                      <Checkbox
                        id="search-visit-badge"
                        label="訪問バッジ対象"
                      />
                    </div>
                    <div className="flex gap-x-1 items-center text-sm">
                      <TipsIcon fill="#999" className="w-3 h-3" />
                      訪問バッジ対象店とは
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center">
              <Button
                variant="function"
                onClick={onClickSearchRestaurants}
                disabled={searchRestaurantsInputValue.length == 0}
              >
                検索する
              </Button>
            </div>
          </div>
        </ContainerInset>

        {searchRestaurantsQuery && (
          <ContainerInset>
            <div className="mb-4">
              <Heading variant="h3-strong" component="h2">
                {flattenSearchRestaurants &&
                  flattenSearchRestaurants.length > 0 && <>おすすめのお店</>}
                {flattenSearchRestaurants &&
                  flattenSearchRestaurants.length == 0 && <>検索結果</>}
              </Heading>
            </div>
            <InfiniteScroll
              className="flex flex-col mx-auto w-full"
              dataLength={flattenSearchRestaurants?.length ?? 0}
              next={() => {
                searchRestaurants.setSize((size) => size + 1)
              }}
              hasMore={
                (searchRestaurants.data &&
                  searchRestaurants.data[searchRestaurants.data.length - 1]
                    ?.restaurants.length !== 0) ??
                false
              }
              loader={<div className="loader" key={0} />}
            >
              {flattenSearchRestaurants &&
                flattenSearchRestaurants.length > 0 &&
                flattenSearchRestaurants.map((restaurant, i) => {
                  return (
                    <div key={restaurant.restaurantId}>
                      <Link href={`/restaurants/${restaurant.restaurantId}`}>
                        <div className="grid grid-cols-3 gap-2">
                          {thumbnailImages(restaurant).map((image, i) => {
                            return (
                              <div
                                className="relative w-full pb-[100%]"
                                key={i}
                              >
                                <Image
                                  unoptimized
                                  alt={`投稿画像${i + 1}`}
                                  fill
                                  className="object-cover rounded"
                                  src={
                                    image
                                      ? image.imageUrl
                                      : config.noImageUrl.userProfileIcon
                                  }
                                />
                              </div>
                            )
                          })}
                        </div>
                        <div className="font-bold my-1">
                          {restaurant.restaurantName}
                        </div>
                        <div className="text-xs opacity-50">
                          {restaurantAreaAndGenre(restaurant)}
                        </div>

                        <div
                          className={cx(
                            'w-full my-5',
                            flattenSearchRestaurants &&
                              flattenSearchRestaurants.length - 1 !== i &&
                              'border-b border-gray'
                          )}
                        />
                      </Link>
                    </div>
                  )
                })}
            </InfiniteScroll>
            {flattenSearchRestaurants?.length == 0 && (
              <>
                <p>一致する検索結果はありませんでした</p>
              </>
            )}
          </ContainerInset>
        )}
      </Container>
      <Footer />
    </>
  )
}

export default Page
